/* ============================================================
 * Melinda Maria — Gift With Purchase
 * ------------------------------------------------------------
 * Handles:
 *   1. Adding a gift product from the cart drawer GWP rail
 *      (keeps the drawer open, refreshes the cart section).
 *   2. Intercepting the drawer Checkout button when the cart
 *      meets the threshold but no gift is in cart — shows an
 *      inline confirmation with a "No thanks" decline option.
 *
 * Extracted from snippets/mm_gwp-cart-drawer.liquid so that
 * Horizon's morph algorithm (which refreshes cart-items-component
 * on every cart change) doesn't have to walk a large inline
 * <script> block on every update.
 *
 * Loaded conditionally from snippets/mm_gwp.liquid only when
 * settings.gwp_enabled is true — zero footprint otherwise.
 * ============================================================ */

(() => {
  // Avoid double-binding if the script tag is accidentally included twice.
  if (window.__mmGwpBound) return;
  window.__mmGwpBound = true;

  /* ---------- helpers ---------- */

  function findCartItemsComponent(el) {
    return (
      el.closest('cart-items-component') ||
      document.querySelector('cart-items-component')
    );
  }

  const GIFT_ADD_SOURCE = 'mm-gwp-add';

  async function getCart() {
    // Cache-bust with timestamp + no-store to defeat any browser/CDN
    // caching that was returning stale cart state right after a delete.
    const res = await fetch('/cart.js?t=' + Date.now(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
    });
    return res.json();
  }

  /**
   * Cart total for threshold comparison — excludes gift card line items.
   * Gift cards are currency, not qualifying merchandise. Uses
   * final_line_price so the value reflects line-level discounts (like
   * the BXGY auto-discount on the gift itself).
   *
   * Must match the Liquid side in snippets/mm_gwp-cart-drawer.liquid.
   */
  function computeThresholdTotal(cart, giftProductIds) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    let total = 0;
    for (const item of cart.items) {
      if (item && item.gift_card === true) continue;
      if (giftProductIds && giftProductIds.has(String(item.product_id))) continue;
      total += Number(item.final_line_price || item.line_price || 0);
    }
    return total;
  }

  /**
   * Collect all cart-items-component section IDs so the /cart/add.js
   * response returns their updated HTML. Matches Horizon's pattern in
   * component-cart-items.js so a single cart:update dispatch refreshes
   * every cart surface on the page (drawer + /cart if both are present).
   */
  function collectCartSectionIds() {
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) {
        sections.add(c.dataset.sectionId);
      }
    });
    return Array.from(sections);
  }

  /**
   * POST /cart/add.js asking for the updated sections HTML in the response.
   * The response becomes the payload for a cart:update dispatch, which
   * Horizon's cart-items-component consumes to morph itself atomically —
   * no stale-state race between our AJAX and Horizon's section render.
   */
  async function addVariant(variantId) {
    const sections = collectCartSectionIds();

    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    };

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to add GWP item');
    return res.json();
  }

  /**
   * Dispatch the Horizon-native cart:update event so every cart surface
   * (cart-items-component, cart-icon, sticky add-to-cart, etc.) updates
   * in sync. Tagged with sourceId so our own auto-remove listener knows
   * this is a native-flow update, not something to re-process.
   */
  function dispatchCartUpdate(cartResponse, source) {
    // /cart/add.js response shape: { token, note, items, items_count,
    //   total_price, ..., sections: { [sectionId]: html, ... } }
    // item_count may or may not be present — fall back to items.length.
    const itemCount =
      typeof cartResponse.item_count === 'number'
        ? cartResponse.item_count
        : Array.isArray(cartResponse.items)
          ? cartResponse.items.length
          : 0;

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartResponse,
          sourceId: source,
          data: {
            source,
            itemCount,
            sections: cartResponse.sections || {},
          },
        },
      })
    );
  }

  /* ---------- 1. Add-gift click ---------- */

  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest('[data-gwp-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Stop double-clicks while the request is in flight.
      btn.disabled = true;

      try {
        const response = await addVariant(variantId);

        // Let Horizon's native cart:update flow morph every cart surface
        // using the authoritative sections HTML in the response. The new
        // HTML has data-gwp-has-gift="true" → component auto-hides → no
        // second click possible.
        dispatchCartUpdate(response, GIFT_ADD_SOURCE);

        // Auto-dismiss any open confirmation prompt.
        hideAllConfirms();
      } catch (err) {
        console.error('[mm_gwp] Failed to add gift:', err);
      } finally {
        btn.disabled = false;
      }
    },
    true
  );

  /* ============================
     Checkout-intercept confirmation
     ============================ */

  const DECLINED_KEY = 'mm_gwp_declined_v1';

  function isDeclined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function setDeclined() {
    try {
      sessionStorage.setItem(DECLINED_KEY, '1');
    } catch (_) {
      /* ignore private mode etc. */
    }
  }

  /**
   * Returns true when the cart is in the "pressure state": threshold met,
   * at least one eligible gift is in stock, and the customer has not added
   * a gift to cart yet. Relies on data-* attrs rendered by liquid.
   */
  function isPressureState(gwpRoot) {
    if (!gwpRoot) return false;
    if (gwpRoot.hidden) return false;
    return (
      gwpRoot.dataset.gwpUnlocked === 'true' &&
      gwpRoot.dataset.gwpHasGift === 'false' &&
      gwpRoot.dataset.gwpAnyInStock === 'true'
    );
  }

  function showConfirm(gwpRoot, checkoutBtn) {
    const confirmEl = gwpRoot.querySelector('[data-gwp-confirm]');
    if (!confirmEl) return;

    confirmEl.hidden = false;
    gwpRoot.classList.add('is-prompting');

    // Gate the checkout button while prompt is visible.
    if (checkoutBtn) {
      checkoutBtn.dataset.mmGwpGated = '1';
      checkoutBtn.setAttribute('aria-disabled', 'true');
    }

    // Bring the rail into view so the Add buttons are obvious.
    const rail = gwpRoot.querySelector('.gwp-scrollbox');
    if (rail && rail.scrollIntoView) {
      try {
        rail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (_) {
        rail.scrollIntoView();
      }
    }

    // Move focus to the decline button for keyboard users.
    const declineBtn = confirmEl.querySelector('[data-gwp-decline]');
    if (declineBtn) {
      // Defer to let the unhide settle before focusing.
      setTimeout(() => declineBtn.focus(), 0);
    }
  }

  function hideConfirm(gwpRoot) {
    if (!gwpRoot) return;
    const confirmEl = gwpRoot.querySelector('[data-gwp-confirm]');
    if (confirmEl) confirmEl.hidden = true;
    gwpRoot.classList.remove('is-prompting');

    // Ungate any checkout buttons we marked.
    document.querySelectorAll('button[data-mm-gwp-gated]').forEach((b) => {
      delete b.dataset.mmGwpGated;
      b.removeAttribute('aria-disabled');
    });
  }

  function hideAllConfirms() {
    document.querySelectorAll('[data-gwp]').forEach((r) => hideConfirm(r));
  }

  /* 2. Intercept the cart drawer's Checkout button. */
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('button[name="checkout"], #checkout');
      if (!btn) return;

      // Only cart drawer checkouts — /cart page and accelerated checkout pass through.
      const drawer = btn.closest('cart-drawer-component');
      if (!drawer) return;

      if (isDeclined()) return;

      // Second click while prompt is open — re-focus decline, don't let through.
      if (btn.dataset.mmGwpGated === '1') {
        e.preventDefault();
        e.stopPropagation();
        const gwpRoot = drawer.querySelector('[data-gwp]');
        const declineBtn = gwpRoot && gwpRoot.querySelector('[data-gwp-decline]');
        if (declineBtn) declineBtn.focus();
        return;
      }

      const gwpRoot = drawer.querySelector('[data-gwp]');
      if (!isPressureState(gwpRoot)) return;

      e.preventDefault();
      e.stopPropagation();
      showConfirm(gwpRoot, btn);
    },
    true
  );

  /* 3. "No thanks" — record decline, hide prompt. */
  document.addEventListener(
    'click',
    (e) => {
      const decline = e.target.closest('[data-gwp-decline]');
      if (!decline) return;

      e.preventDefault();
      e.stopPropagation();

      setDeclined();
      const gwpRoot = decline.closest('[data-gwp]');
      hideConfirm(gwpRoot);
    },
    true
  );

  /* 4. Esc while prompt is visible = same as decline. */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector(
      '[data-gwp].is-prompting [data-gwp-confirm]:not([hidden])'
    );
    if (!open) return;

    e.preventDefault();
    e.stopPropagation();
    setDeclined();
    hideConfirm(open.closest('[data-gwp]'));
  });

  /* ============================
     Auto-remove gift when cart drops below threshold

     Listens for Horizon's 'cart:update' event (dispatched by
     cart-items-component on every cart change). If the cart
     contains a gift but is now below threshold, removes the
     gift line via /cart/change.js, then re-dispatches
     'cart:update' so downstream components (cart icon, cart
     drawer, totals) stay in sync.
     ============================ */

  const AUTO_REMOVE_SOURCE = 'mm-gwp-auto-remove';

  // Single-flight guard: prevents rapid events from firing multiple removes.
  // Set synchronously (before any await) to avoid races.
  let autoRemovingGift = false;

  /**
   * Change a cart line's quantity (0 = remove). Mirrors the payload that
   * Horizon's component-cart-items.js sends so the response includes the
   * section HTML other components need to update themselves.
   */
  async function changeLineQuantity(lineKey, quantity) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = new Set();
    cartItemsComponents.forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) {
        sections.add(c.dataset.sectionId);
      }
    });

    const body = {
      id: lineKey,
      quantity,
      sections: Array.from(sections).join(','),
      sections_url: window.location.pathname,
    };

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to change line quantity');
    return res.json();
  }

  document.addEventListener('cart:update', async (e) => {
    const source = e && e.detail && e.detail.data && e.detail.data.source;

    if (source === AUTO_REMOVE_SOURCE) return;
    if (source === GIFT_ADD_SOURCE) return;
    if (autoRemovingGift) return;

    const gwpRoot = document.querySelector('[data-gwp]');
    if (!gwpRoot) return;

    const thresholdCents = Number(gwpRoot.dataset.gwpThreshold || 0);
    if (!thresholdCents) return;

    autoRemovingGift = true;
    try {
      // Horizon's cart:update event carries the fresh cart in e.detail.resource
      // (it's the direct response from /cart/change.js — post-commit, authoritative).
      // This bypasses any /cart.js caching that was returning stale totals.
      let cart = e && e.detail && e.detail.resource;
      if (!cart || !Array.isArray(cart.items) || typeof cart.total_price !== 'number') {
        try {
          cart = await getCart();
        } catch (_) {
          return;
        }
      }
      if (!cart || !Array.isArray(cart.items)) return;

      // Build gift product IDs FIRST so the threshold calculation can
      // exclude them. Once the BXGY discount is live, Shopify removes
      // the discount when cart drops below threshold, which restores the
      // gift to full price. If the gift's full price stayed in the
      // threshold total, it would keep the cart "above threshold" and
      // prevent auto-remove — so we have to subtract the gift line.
      const giftProductIds = new Set();
      gwpRoot.querySelectorAll('[data-product-id]').forEach((el) =>
        giftProductIds.add(String(el.dataset.productId))
      );
      if (giftProductIds.size === 0) return;

      const totalCents = computeThresholdTotal(cart, giftProductIds);
      if (totalCents >= thresholdCents) return;

      const giftLines = cart.items.filter((it) =>
        giftProductIds.has(String(it.product_id))
      );
      if (giftLines.length === 0) return;

      let lastResponse = null;
      for (const line of giftLines) {
        try {
          lastResponse = await changeLineQuantity(line.key, 0);
        } catch (err) {
          console.error('[mm_gwp] Failed to remove gift line:', err);
        }
      }

      // Re-dispatch cart:update so cart icon, drawer, and other components
      // refresh against the authoritative new cart state.
      if (lastResponse) {
        const newItemCount = Number(lastResponse.item_count || 0);
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: lastResponse,
              sourceId: AUTO_REMOVE_SOURCE,
              data: {
                source: AUTO_REMOVE_SOURCE,
                itemCount: newItemCount,
                sections: lastResponse.sections || {},
              },
            },
          })
        );
      }
    } finally {
      autoRemovingGift = false;
    }
  });

})();
